import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Tabs, 
  Tab, 
  Paper,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Comment as CommentIcon,
  Assignment as AssignmentIcon,
  VerifiedUser as VerifiedUserIcon,
  InsertDriveFile,
  Loop,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import TaskTrackingHistory from './TaskTrackingHistory';
import TaskRecurrenceHistory from './TaskRecurrenceHistory';
import TaskAttachments from './TaskAttachments';

import { useTaskTracking } from '../../../hooks/useTaskTracking';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Placeholder() {
  const { t } = useTranslation(['common']);
  return (
    <Box>
      <Typography>{t('common:coming_soon')}</Typography>
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `task-tab-${index}`,
    'aria-controls': `task-tabpanel-${index}`,
  };
}

export default function TaskTabs({ taskId, task, update_task, doUpdate }) {
  const { t } = useTranslation(['common', 'tasks']);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    taskRecurrenceHistory,
    loading: recurrenceLoading,
    error: recurrenceError,
    clearError: clearRecurrenceError,
    fetchTaskRecurrenceHistory,
  } = useTaskTracking();

  // Load recurrence history - pagination params forwarded from the child component
  const loadRecurrenceHistory = useCallback(async (params = {}) => {
    if (!taskId) return;
    try {
      await fetchTaskRecurrenceHistory(taskId, params);
    } catch (err) {
      console.error('Failed to load recurrence history:', err);
    }
  }, [taskId, fetchTaskRecurrenceHistory]);

  const tabs = [
    {
      label: t('tasks:tabs.recurrence'),
      icon: Loop,
      component: TaskRecurrenceHistory,
      description: t('tasks:tabs.recurrence_description'),
      key: 'recurrence'
    },
    {
      label: t('tasks:tabs.activity_log'),
      icon: VerifiedUserIcon,
      component: TaskTrackingHistory,
      description: t('tasks:tabs.activity_description'),
      key: 'activity'
    },
    {
      label: t('tasks:tabs.files', { count: task.attachment_count }),
      icon: InsertDriveFile,
      component: TaskAttachments,
      description: t('tasks:tabs.files_description'),
      key: 'files'
    },
    {
      label: t('tasks:tabs.comments', { count: 0 }),
      icon: CommentIcon,
      component: Placeholder,
      description: t('tasks:tabs.comments_description'),
      key: 'comments'
    }
  ];

  // Get current tab from URL params or default to first tab
  const getTabFromParams = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = tabs.findIndex(tab => tab.key === tabParam);
      return tabIndex !== -1 ? tabIndex : 0;
    }
    return 0;
  };

  const [currentTab, setCurrentTab] = useState(getTabFromParams);

  // Update tab when URL params change (e.g., browser back/forward)
  useEffect(() => {
    setCurrentTab(getTabFromParams());
  }, [searchParams]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    
    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabs[newValue].key);
    setSearchParams(newSearchParams);
  };

  return (
    <Box sx={{ width: '100%', minHeight: "800px" }}>
      {/* Tabs Navigation */}
      <Paper 
        elevation={0} 
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="task details tabs"
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: isMobile ? 56 : 64,
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 1.5 : 3,
              '&.Mui-selected': {
                fontWeight: 600,
                bgcolor: 'background.paper'
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          {tabs.map((tab, index) => {
            const IconComponent = tab.icon;
            return (
              <Tab
                key={index}
                icon={<IconComponent sx={{ fontSize: isMobile ? 34 : 36 }} />}
                iconPosition="start"
                label={
                  <Box sx={{ textAlign: 'left', ml: isMobile ? 0.5 : 1, pb: 1 }}>
                    <Typography variant="body2" fontWeight="inherit">
                      {tab.label}
                    </Typography>
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {tab.description}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minWidth: "unset"
                }}
                {...a11yProps(index)}
              />
            );
          })}
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ bgcolor: 'background.paper', p: 4 }}>
          {tabs.map((tab, index) => {
            const TabComponent = tab.component;
            return (
              <TabPanel key={index} value={currentTab} index={index}>
                <TabComponent 
                  taskId={taskId} 
                  task={task} 
                  update_task={update_task} 
                  doUpdate={doUpdate} 

                  loadRecurrenceHistory={loadRecurrenceHistory}
                  taskRecurrenceHistory={taskRecurrenceHistory}
                  recurrenceLoading={recurrenceLoading}
                  recurrenceError={recurrenceError}
                  clearRecurrenceError={clearRecurrenceError}
                />
              </TabPanel>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}